"use client";

import { ArrowRight, CheckCircle2, Loader2 } from "lucide-react";
import { usePostHog } from "posthog-js/react";
import { type FormEvent, useRef, useState } from "react";

type SubmissionState = "idle" | "submitting" | "success" | "error";

function createIdempotencyKey() {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }

  return `schools-${Date.now()}-${Math.random().toString(36).slice(2, 18)}`;
}

export function SchoolInterestForm() {
  const posthog = usePostHog();
  const hasTrackedStart = useRef(false);
  const idempotencyKey = useRef(createIdempotencyKey());
  const [submissionState, setSubmissionState] = useState<SubmissionState>("idle");
  const [errorMessage, setErrorMessage] = useState("");

  const handleStart = () => {
    if (hasTrackedStart.current) return;

    hasTrackedStart.current = true;
    posthog?.capture("schools_lead_form_started", { placement: "schools_page" });
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmissionState("submitting");
    setErrorMessage("");

    const form = event.currentTarget;
    const formData = new FormData(form);
    const payload = {
      idempotencyKey: idempotencyKey.current,
      name: formData.get("name"),
      email: formData.get("email"),
      organization: formData.get("organization"),
      learnerCount: formData.get("learnerCount"),
      languages: formData.get("languages"),
      timing: formData.get("timing"),
      question: formData.get("question"),
      website: formData.get("website"),
      sourcePath: window.location.pathname,
    };

    try {
      const response = await fetch("/api/schools/interest", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const result = (await response.json().catch(() => null)) as { error?: string } | null;
        throw new Error(result?.error || "We could not send your request.");
      }

      setSubmissionState("success");
      form.reset();
      posthog?.capture("schools_lead_submitted", {
        placement: "schools_page",
        learner_count_range:
          typeof payload.learnerCount === "string" ? payload.learnerCount : "not_provided",
        pilot_timing: typeof payload.timing === "string" ? payload.timing : "not_provided",
      });
    } catch (error) {
      setSubmissionState("error");
      setErrorMessage(
        error instanceof Error ? error.message : "We could not send your request. Please try again."
      );
    }
  };

  if (submissionState === "success") {
    return (
      <div
        className="flex min-h-[360px] flex-col items-center justify-center rounded-[28px] border border-[#e8e1d8] bg-white p-8 text-center"
        role="status"
      >
        <span className="flex h-14 w-14 items-center justify-center rounded-full bg-[#fff0e7] text-[#c84f1f]">
          <CheckCircle2 className="h-7 w-7" aria-hidden />
        </span>
        <h3 className="mt-6 text-2xl font-black text-[#171717]">Thanks—we have your request.</h3>
        <p className="mt-3 max-w-md leading-7 text-[#6f6b64]">
          We will read the details and reply by email with the next step.
        </p>
      </div>
    );
  }

  const fieldClass =
    "mt-2 min-h-12 w-full rounded-2xl border border-[#dcd4ca] bg-[#fffdf9] px-4 py-3 text-[15px] text-[#171717] outline-none transition placeholder:text-[#8a857e] focus:border-[#f26b2d] focus:ring-4 focus:ring-[#f26b2d]/10";

  return (
    <form
      onSubmit={handleSubmit}
      onFocus={handleStart}
      className="rounded-[28px] border border-[#e8e1d8] bg-white p-5 shadow-[0_20px_60px_rgba(30,20,10,0.08)] sm:p-8"
    >
      <div className="grid gap-5 sm:grid-cols-2">
        <label className="text-sm font-bold text-[#3b3731]">
          Your name <span className="text-[#c84f1f]">*</span>
          <input
            className={fieldClass}
            name="name"
            type="text"
            autoComplete="name"
            required
            maxLength={100}
            placeholder="Alex Morgan"
          />
        </label>
        <label className="text-sm font-bold text-[#3b3731]">
          Work email <span className="text-[#c84f1f]">*</span>
          <input
            className={fieldClass}
            name="email"
            type="email"
            autoComplete="email"
            required
            maxLength={254}
            placeholder="alex@school.org"
          />
        </label>
      </div>

      <label className="mt-5 block text-sm font-bold text-[#3b3731]">
        School or organisation <span className="text-[#c84f1f]">*</span>
        <input
          className={fieldClass}
          name="organization"
          type="text"
          autoComplete="organization"
          required
          maxLength={160}
          placeholder="Your language school"
        />
      </label>

      <div className="mt-5 grid gap-5 sm:grid-cols-2">
        <label className="text-sm font-bold text-[#3b3731]">
          How many students?
          <select className={fieldClass} name="learnerCount" defaultValue="">
            <option value="">Not sure yet</option>
            <option value="under_25">Under 25</option>
            <option value="25_99">25–99</option>
            <option value="100_499">100–499</option>
            <option value="500_plus">500+</option>
          </select>
        </label>
        <label className="text-sm font-bold text-[#3b3731]">
          When would you like to start?
          <select className={fieldClass} name="timing" defaultValue="">
            <option value="">Still exploring</option>
            <option value="within_30_days">Within 30 days</option>
            <option value="one_to_three_months">1–3 months</option>
            <option value="three_to_six_months">3–6 months</option>
            <option value="later">Later</option>
          </select>
        </label>
      </div>

      <label className="mt-5 block text-sm font-bold text-[#3b3731]">
        Language or languages
        <input
          className={fieldClass}
          name="languages"
          type="text"
          maxLength={200}
          placeholder="For example, French and Dutch"
        />
      </label>

      <label className="mt-5 block text-sm font-bold text-[#3b3731]">
        Which course route are you considering?
        <textarea
          className={`${fieldClass} min-h-28 resize-y`}
          name="question"
          maxLength={1200}
          placeholder="Tell us whether you want to use a ChickyTutor course or add speaking practice to your own. You can also share what the class is learning."
        />
      </label>

      <label className="absolute -left-[9999px] top-auto h-px w-px overflow-hidden" aria-hidden>
        Website
        <input name="website" type="text" tabIndex={-1} autoComplete="off" aria-hidden="true" />
      </label>

      {submissionState === "error" && (
        <p
          className="mt-5 rounded-2xl bg-red-50 px-4 py-3 text-sm font-semibold text-red-700"
          role="alert"
        >
          {errorMessage}
        </p>
      )}

      <button
        type="submit"
        disabled={submissionState === "submitting"}
        className="commercial-button commercial-button-primary mt-6 w-full disabled:cursor-wait disabled:opacity-70"
      >
        {submissionState === "submitting" ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
            Sending request
          </>
        ) : (
          <>
            Tell us about your class
            <ArrowRight className="ml-2 h-4 w-4" aria-hidden />
          </>
        )}
      </button>
      <p className="mt-4 text-center text-xs leading-5 text-[#6f6b64]">
        We use these details only to respond about a school pilot. No mailing-list opt-in is added.
      </p>
    </form>
  );
}
